import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, ChevronLeft, CircleDot, Circle, Square } from "lucide-react";

import { Button, WaveBackdrop } from "@/components/kit";
import { FIELDS, QUESTIONNAIRE } from "@/lib/options";
import { useApp } from "@/store";

export default function Questionnaire() {
  const { profile, saveProfile, completeOnboarding, toast } = useApp();
  const [draft, setDraft] = useState({ ...profile });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const key = QUESTIONNAIRE[step];
  const field = FIELDS[key];
  const value = draft[key];
  const last = step === QUESTIONNAIRE.length - 1;
  const canBack = step > 0 || window.history.length > 1;

  const finish = async (next) => {
    setSaving(true);
    try {
      await saveProfile({ ...next, source: next.source ?? "questionnaire" });
      await completeOnboarding();
      navigate("/scan", { replace: true });
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };
  const next = () => (last ? finish(draft) : setStep(step + 1));
  const select = (v) => {
    if (field.multi) {
      const arr = value ?? [];
      setDraft({ ...draft, [key]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
    } else setDraft({ ...draft, [key]: value === v ? null : v });
  };

  return (
    <div data-testid="questionnaire-screen" className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-off">
      <WaveBackdrop />
      <div className="relative mx-auto flex w-full max-w-xl items-center gap-2 px-3 pt-4">
        <button type="button" data-testid="questionnaire-back" aria-label="Back" disabled={!canBack} onClick={() => (step > 0 ? setStep(step - 1) : navigate(-1))} className={`focus-ring flex h-11 min-w-[56px] items-center justify-center rounded-full ${canBack ? "text-navy" : "invisible"}`}>
          <ChevronLeft className="h-[22px] w-[22px]" />
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded bg-tert" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={QUESTIONNAIRE.length}>
          <div className="h-1.5 rounded bg-ocean transition-all duration-300" style={{ width: `${((step + 1) / QUESTIONNAIRE.length) * 100}%` }} />
        </div>
        <button type="button" data-testid="questionnaire-skip-all" onClick={() => finish(draft)} className="focus-ring h-11 min-w-[56px] rounded-full px-2 t-label !text-ocean">Skip all</button>
      </div>
      <div key={key} className="animate-fade-up relative mx-auto w-full max-w-xl flex-1 px-6 pt-6">
        <p className="t-label">Question {step + 1} of {QUESTIONNAIRE.length} · Optional</p>
        <h1 data-testid="questionnaire-question" className="t-h1 mt-2">{field.title}</h1>
        {field.help ? <p className="t-body mt-2">{field.help}</p> : null}
        <div className="mt-6 flex flex-col gap-3" role={field.multi ? "group" : "radiogroup"} aria-label={field.title}>
          {field.options.map((o) => {
            const sel = field.multi ? (value ?? []).includes(o.v) : value === o.v;
            const Icon = sel ? (field.multi ? CheckSquare : CircleDot) : field.multi ? Square : Circle;
            return (
              <button
                key={o.v}
                type="button"
                role={field.multi ? "checkbox" : "radio"}
                aria-checked={sel}
                data-testid={`q-option-${key}-${o.v}`}
                onClick={() => select(o.v)}
                className={`focus-ring flex min-h-[60px] items-center justify-between rounded-[20px] border px-4 transition-all active:scale-[0.99] ${sel ? "border-navy bg-navy" : "border-line bg-white hover:border-ocean"}`}
              >
                <span className={`t-title ${sel ? "!text-white" : ""}`}>{o.l}</span>
                <Icon className={`h-[22px] w-[22px] ${sel ? "text-ocean" : "text-sky"}`} aria-hidden />
              </button>
            );
          })}
        </div>
      </div>
      <div className="relative mx-auto flex w-full max-w-xl gap-3 px-6 pb-6 pt-3">
        <Button testId="questionnaire-skip" title="Skip" variant="outline" onClick={next} className="flex-1" disabled={saving} />
        <Button testId="questionnaire-next" title={last ? "Finish" : "Next"} onClick={next} loading={saving} className="flex-[2]" disabled={!value || (Array.isArray(value) && value.length === 0)} />
      </div>
    </div>
  );
}
